import * as logger from "../logger";
import { AppType, getAppType } from "./nativeHelpers";

/**
 * Native asset selection interop (MYG-133100).
 *
 * DriveNative has its own asset selection screen. Two things follow:
 *
 *   1. When the driver taps an asset selection entry point in this web app and
 *      the native screen is available, native should take over rather than the
 *      WebView rendering its own page.
 *   2. When the driver changes trailers or shipments on the native screen, this
 *      app has to hear about it — otherwise its cached view of the vehicle is
 *      silently stale.
 */

export const TRAILERS_CHANGED_EVENT = "geotab.trailersChanged";
export const SHIPMENTS_CHANGED_EVENT = "geotab.shipmentsChanged";
export const DEVICE_CHANGED_EVENT = "geotab.deviceChanged";

/**
 * How long to wait for native to answer before falling back to the WebView page.
 *
 * The bridge is a callback, not a promise, so a native binary that predates the
 * `assets` module — or one that throws before invoking the callback — would
 * otherwise leave the tap hanging with no page and no error. Falling back after
 * a short wait keeps the entry point responsive on every build.
 */
const OPEN_NATIVE_TIMEOUT_MS = 2000;

/**
 * Ask the native shell to open its asset selection screen.
 *
 * Resolves `true` when native took over and the caller should do nothing more,
 * `false` when the caller must navigate to `assets/main` itself. Never rejects:
 * every failure path is a fallback, not an error the driver should see.
 */
export function openNativeAssetSelection(): Promise<boolean> {
    const assets = window.geotabModules?.assets;

    // Not the native shell, or a native build without the assets module.
    if (getAppType() !== AppType.NativeSdk || !assets?.openNativeSelection) {
        return Promise.resolve(false);
    }

    return new Promise<boolean>(resolve => {
        let settled = false;
        const settle = (handled: boolean) => {
            if (settled) {
                return;
            }
            settled = true;
            resolve(handled);
        };

        const timer = window.setTimeout(() => {
            logger.log(logger.Source.NATIVE, "openNativeAssetSelection: native did not respond, using WebView page");
            settle(false);
        }, OPEN_NATIVE_TIMEOUT_MS);

        try {
            assets.openNativeSelection(null, (error?: Error, handled?: boolean) => {
                window.clearTimeout(timer);
                if (error) {
                    logger.error(logger.Source.NATIVE, `openNativeAssetSelection: ${ error.message }`);
                    settle(false);
                    return;
                }
                settle(!!handled);
            });
        } catch (error) {
            window.clearTimeout(timer);
            logger.error(logger.Source.NATIVE, `openNativeAssetSelection threw: ${ (error as Error).message }`);
            settle(false);
        }
    });
}

/**
 * Open asset selection, preferring the native screen.
 *
 * Call this from driver-facing entry points only — the dashboard vehicle tile,
 * the More page's Assets link. Do NOT use it for internal returns to
 * `assets/main` (a cancelled logout, the end of a trailer or shipment
 * workflow): those are mid-flow navigations inside this app, and handing them
 * to native would drop the driver out of the flow they are in.
 *
 * `navigate` is invoked only when native declines.
 */
export function navigateToAssetSelection(navigate: () => void): void {
    void openNativeAssetSelection().then(handled => {
        if (!handled) {
            navigate();
        }
    });
}

/**
 * Listen for asset changes made on the native screen; returns an unsubscribe
 * function. For trailers/shipments, native's payload is display-only (not a
 * full model), so the caller re-syncs those entities rather than writing it
 * into the store. For a vehicle change the native shell is the source of
 * truth, so the pushed `deviceId` (or `NoDeviceId`) is forwarded for the
 * caller to adopt directly.
 */
export function registerNativeAssetChangeListeners(
    onAssetsChanged: (
        changed: "trailers" | "shipments" | "vehicle",
        deviceId?: string | null
    ) => void
): () => void {
    const onTrailers = () => onAssetsChanged("trailers");
    const onShipments = () => onAssetsChanged("shipments");
    const onVehicle = (event: Event) => {
        const detail = (event as CustomEvent<{ deviceId?: string | null }>).detail;
        onAssetsChanged("vehicle", detail?.deviceId ?? null);
    };

    window.addEventListener(TRAILERS_CHANGED_EVENT, onTrailers);
    window.addEventListener(SHIPMENTS_CHANGED_EVENT, onShipments);
    window.addEventListener(DEVICE_CHANGED_EVENT, onVehicle);

    // Tells native it's now safe to push: the earlier `onWebViewLoaded` signal
    // fired before these listeners existed, so a push retried on it still
    // landed with nothing attached to receive it.
    window.__rnBridgeNotify?.("assets", "listenersReady", null);

    return () => {
        window.removeEventListener(TRAILERS_CHANGED_EVENT, onTrailers);
        window.removeEventListener(SHIPMENTS_CHANGED_EVENT, onShipments);
        window.removeEventListener(DEVICE_CHANGED_EVENT, onVehicle);
    };
}
